/**
 * Track Page — GE Lines
 *
 * Public shipment tracking search form.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Ship } from 'lucide-react';
import { useStorefront } from '@/context/StorefrontContext';
import { useSurfaceAccess } from '@/context/SurfaceAccessContext';

export function TrackPage() {
  const [ref, setRef] = useState('');
  const navigate = useNavigate();
  const { brand } = useStorefront();
  const { ensureAccess } = useSurfaceAccess();

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = ref.trim();
    if (!trimmed) return;
    const allowed = await ensureAccess('tracking');
    if (allowed) navigate(`/track/${encodeURIComponent(trimmed)}`);
  };

  return (
    <div className="min-h-[calc(100vh-12rem)] flex items-center justify-center py-16 bg-gradient-to-br from-slate-50 to-brand-50">
      <div className="w-full max-w-xl mx-auto px-4 animate-fade-in">
        <div className="text-center mb-10">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-brand-700 to-ocean-500 flex items-center justify-center">
            <Ship className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Track Your Shipment</h1>
          <p className="text-slate-500">
            Enter your container number, B/L number, AWB, or booking reference
          </p>
        </div>

        <form
          onSubmit={handleSearch}
          className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100"
        >
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Reference Number
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={ref}
              onChange={e => setRef(e.target.value)}
              placeholder="e.g. TGHU1234567, MSCUBL123456, 176-12345678"
              className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-brand-500 focus:outline-none text-sm"
              autoFocus
            />
            <button
              type="submit"
              disabled={!ref.trim()}
              className="px-5 py-3 bg-brand-700 text-white rounded-xl hover:bg-brand-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              Track
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-400">
            Accepted formats: container number, B/L number, AWB, or booking reference for {brand.brandShortName}
          </p>
        </form>
      </div>
    </div>
  );
}
